import React, { useId } from 'react'
import { CheckBoxFormControl, CheckBoxInput } from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { faDash } from '@fortawesome/pro-solid-svg-icons/faDash'
import { Control, Controller, ControllerProps } from 'react-hook-form'
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

export type CheckedStatus = boolean | 'mixed'
export interface CheckBoxProps extends Omit<React.HTMLProps<HTMLSpanElement>, 'checkedStatus' | 'onChange'> {
  onChange?: (checkedStatus: CheckedStatus) => void
  checkedStatus: CheckedStatus
  description?: string
  label?: string
  labelId?: string
  disabled?: boolean
  className?: string
  gutterBottom?: boolean
  testId?: string
  checkedIcon?: IconDefinition
  notCheckedIcon?: IconDefinition
  readonly?: boolean
  hint?: string
}

const CheckBox: React.FC<CheckBoxProps> = ({
  onChange,
  checkedStatus,
  label,
  description,
  labelId,
  disabled,
  gutterBottom,
  className,
  testId,
  checkedIcon,
  notCheckedIcon,
  readonly,
  hint,
  ...otherProps
}) => {
  const generatedId = useId()
  const inputId = `${generatedId}-input`

  const handleClick = () => {
    if (disabled || readonly) return
    if (checkedStatus === 'mixed') {
      onChange?.(true)
    } else {
      onChange?.(!checkedStatus)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.code === 'Space' || event.code === 'Enter') {
      event.preventDefault()
      handleClick()
    }
  }

  return (
    <CheckBoxFormControl
      label={label}
      inputId={inputId}
      hiddenLabel={!label}
      gutterBottom={gutterBottom}
      disabled={disabled}
      className={`${className ?? ''}${checkedStatus ? ' checked' : ''}${disabled ? ' disabled' : ''}${
        readonly ? ' readonly' : ''
      }`}
      hint={hint && { title: hint, position: 'afterLabel' }}
    >
      <CheckBoxInput
        data-testid={`${testId}-checkbox`}
        role="checkbox"
        aria-checked={checkedStatus}
        aria-readonly={readonly}
        tabIndex={disabled || readonly ? -1 : 0}
        id={inputId}
        aria-labelledby={labelId}
        aria-label={description}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...otherProps}
      >
        {checkedStatus === true && <FontAwesomeIcon icon={checkedIcon || faCheck} />}
        {checkedStatus === false && notCheckedIcon && <FontAwesomeIcon icon={notCheckedIcon} />}
        {checkedStatus === 'mixed' && <FontAwesomeIcon icon={faDash} />}
      </CheckBoxInput>
    </CheckBoxFormControl>
  )
}

export default CheckBox

interface CheckBoxWithControllerProps
  extends Omit<CheckBoxProps, 'checkedStatus' | 'onChange'>,
    Omit<ControllerProps, 'render' | 'children'> {
  control: Control<any>
}
export const CheckBoxWithController: React.FC<CheckBoxWithControllerProps> = ({
  control,
  name,
  rules,
  shouldUnregister,
  defaultValue,
  ...checkBoxProps
}) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={rules}
      shouldUnregister={shouldUnregister}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <CheckBox {...checkBoxProps} checkedStatus={!!value} onChange={onChange} />
      )}
    />
  )
}
